import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSeo } from '../../hooks/useSeo';
import { BlogLayout, Chip } from './BlogLayout';
import { Emphasis } from './Emphasis';
import { getAuthor } from './authors';
import { formatDate, stripEmphasis } from './format';
import { mdxComponents } from './mdxComponents';
import { getTermsForPost } from '../glossary/glossary';
import { getPost, getRelated } from './registry';
import type { BlogPost } from './types';

const SITE_URL = 'https://www.revvly.ai';

/** BlogPosting + BreadcrumbList JSON-LD, generated from the post's own frontmatter. */
function buildArticleJsonLd(post: BlogPost, authorName: string) {
  const url = `${SITE_URL}/blog/${post.slug}`;
  const headline = stripEmphasis(post.title);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        headline,
        description: stripEmphasis(post.excerpt),
        datePublished: post.date,
        dateModified: post.updated ?? post.date,
        ...(post.answer ? { abstract: post.answer } : {}),
        author: { '@type': 'Person', name: authorName },
        publisher: {
          '@type': 'Organization',
          name: 'Revvly',
          url: SITE_URL,
          logo: `${SITE_URL}/favicon.svg`,
        },
        url,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: headline, item: url },
        ],
      },
      ...(post.faq?.length
        ? [
            {
              '@type': 'FAQPage',
              mainEntity: post.faq.map(({ question, answer }) => ({
                '@type': 'Question',
                name: stripEmphasis(question),
                acceptedAnswer: { '@type': 'Answer', text: stripEmphasis(answer) },
              })),
            },
          ]
        : []),
    ],
  };
}

interface TocItem {
  id: string;
  label: string;
}

/**
 * Builds the "On this page" table of contents from the rendered article body
 * (every `<h2>` rehype-slug stamped an id onto) and tracks which heading is in
 * view for the active highlight. Rebuilds whenever the slug changes.
 */
function useArticleToc(slug: string) {
  const proseRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>();

  useEffect(() => {
    const el = proseRef.current;
    if (!el) return;
    const headings = Array.from(el.querySelectorAll('h2[id]')) as HTMLElement[];
    setItems(headings.map((h) => ({ id: h.id, label: h.textContent ?? '' })));
  }, [slug]);

  useEffect(() => {
    if (!items.length || !('IntersectionObserver' in window)) return undefined;
    setActiveId((current) => current ?? items[0].id);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        }
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 },
    );

    items.forEach(({ id }) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [items]);

  return { proseRef, items, activeId };
}

function ShareButtons() {
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
  };

  return (
    <div className="rv-share">
      <button
        aria-label="Share on LinkedIn"
        onClick={() =>
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
            '_blank',
          )
        }
      >
        <svg viewBox="0 0 24 24">
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
          <rect x="2" y="9" width="4" height="12" />
          <circle cx="4" cy="4" r="2" />
        </svg>
        In
      </button>
      <button
        aria-label="Share on X"
        onClick={() =>
          window.open(
            `https://x.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`,
            '_blank',
          )
        }
      >
        <svg viewBox="0 0 24 24">
          <path d="M4 4l16 16M20 4L4 20" />
        </svg>
        X
      </button>
      <button aria-label="Copy link" onClick={copyLink}>
        <svg viewBox="0 0 24 24">
          <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
          <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
        Copy
      </button>
    </div>
  );
}

function NotFound() {
  const navigate = useNavigate();
  return (
    <article className="rv-body-col">
      <header className="rv-art-head">
        <h1>That article doesn't exist (yet).</h1>
        <p className="rv-deck">
          The post you're looking for may have moved or hasn't been published.
        </p>
        <button className="rv-cta-btn" onClick={() => navigate('/blog')}>
          Back to the blog
        </button>
      </header>
    </article>
  );
}

export function BlogArticle() {
  const { slug } = useParams();
  const post = getPost(slug);
  const { proseRef, items: tocItems, activeId } = useArticleToc(slug ?? '');
  const author = post ? getAuthor(post.author) : null;

  // Called unconditionally (rules of hooks): `post` can flip between found and
  // not-found across renders as `slug` changes, so the SEO tags for both cases
  // live in one call rather than one nested inside the early return below.
  useSeo({
    title: post ? `${stripEmphasis(post.title)} — Revvly Blog` : 'Article not found — Revvly Blog',
    description: post
      ? stripEmphasis(post.excerpt)
      : "The post you're looking for may have moved or hasn't been published.",
    path: `/blog/${slug ?? ''}`,
    jsonLd: post && author ? buildArticleJsonLd(post, author.name) : undefined,
  });

  if (!post || !author) {
    return (
      <BlogLayout>
        <NotFound />
      </BlogLayout>
    );
  }

  const related = getRelated(post.slug);
  const keyTerms = getTermsForPost(post.slug);
  const { Content } = post;

  return (
    <BlogLayout>
      <div className="rv-article">
        {/* Left: TOC */}
        {tocItems.length > 0 && (
          <aside className="rv-toc">
            <div className="rv-toc-wrap">
              <p className="rv-section-label">On this page</p>
              <ul className="rv-toc-list">
                {tocItems.map(({ id, label }) => (
                  <li key={id}>
                    <a href={`#${id}`} className={activeId === id ? 'on' : undefined}>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}

        {/* Center: article body */}
        <article className="rv-body-col">
          <header className="rv-art-head">
            <div className="chiprow">
              <Chip label={post.category} />
              <span>{post.readTime}</span>
              <span className="dot" />
              <span>{formatDate(post.date)}</span>
            </div>
            <h1>
              <Emphasis text={post.title} />
            </h1>
            {post.deck && (
              <p className="rv-deck">
                <Emphasis text={post.deck} />
              </p>
            )}
            <div className="rv-byline">
              <div className="rv-avatar">{author.initials}</div>
              <div className="who">
                <div className="name">{author.name}</div>
                <div className="role">{author.role}</div>
              </div>
              <ShareButtons />
            </div>
          </header>

          {post.answer && (
            <section className="rv-answer" aria-label="Short answer">
              <p className="rv-section-label">Short answer</p>
              <p className="rv-answer-text">{post.answer}</p>
              {post.takeaways && post.takeaways.length > 0 && (
                <>
                  <p className="rv-section-label">Key takeaways</p>
                  <ul>
                    {post.takeaways.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}

          <div className="rv-prose" ref={proseRef}>
            <Content components={mdxComponents} />
            {post.faq && post.faq.length > 0 && (
              <>
                <h2 id="faq">Frequently asked questions</h2>
                {post.faq.map(({ question, answer }) => (
                  <div key={question} className="rv-faq-item">
                    <h3>
                      <Emphasis text={question} />
                    </h3>
                    <p>
                      <Emphasis text={answer} />
                    </p>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Author bio */}
          {author.bio && (
            <div className="rv-bio">
              <div className="rv-avatar">{author.initials}</div>
              <div>
                <div className="name">{author.name}</div>
                <div className="role">{author.role}</div>
                <p className="desc">{author.bio}</p>
              </div>
            </div>
          )}
        </article>

        {/* Right: sidebar */}
        <aside className="rv-sidebar">
          <div className="rv-sidebar-wrap">
            <div className="rv-cta-card">
              <h4>See your earned MRR, live.</h4>
              <p>
                Revvly reconciles reported and earned revenue automatically. No spreadsheet, no
                quarter-end scramble.
              </p>
              <button className="btn" onClick={() => (window.location.href = '/early-access')}>
                Get early access
              </button>
            </div>

            {keyTerms.length > 0 && (
              <div>
                <p className="rv-section-label">Key terms</p>
                <div className="rv-related-list">
                  {keyTerms.map((t) => (
                    <Link key={t.slug} className="rv-related-item" to={`/glossary/${t.slug}`}>
                      <p className="t">{t.name}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {related.length > 0 && (
              <div>
                <p className="rv-section-label">Related articles</p>
                <div className="rv-related-list">
                  {related.map((r) => (
                    <Link key={r.slug} className="rv-related-item" to={`/blog/${r.slug}`}>
                      <p className="t">
                        <Emphasis text={r.title} />
                      </p>
                      <span className="m">
                        {r.readTime} · {r.category}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </BlogLayout>
  );
}
